import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/** Top-level dialogs that must not stack unless an explicit exception is granted. */
export type TopLevelDialogOwner = 'notification-modal' | 'way-purchase-modal' | 'feature-modal';

/**
 * Explicit exceptions where a second overlay may appear while another top-level dialog is open.
 * Nested in-modal UI (payment success, tap-to-pay, prepaid-redeemed) is not a second top-level
 * owner and does not need an exception here.
 */
export const DIALOG_STACK_EXCEPTIONS = {
  /** Way purchase overlay on top of open order-request NotificationModal (auto Way lookup). */
  WAY_ON_NOTIFICATION: 'way-on-notification',
} as const;

export type DialogStackException =
  (typeof DIALOG_STACK_EXCEPTIONS)[keyof typeof DIALOG_STACK_EXCEPTIONS];

export interface DialogMutexAcquireOptions {
  /** Allowlisted stack; see {@link DIALOG_STACK_EXCEPTIONS}. */
  exception?: DialogStackException;
}

interface DialogMutexFrame {
  owner: TopLevelDialogOwner;
  exception?: DialogStackException;
}

/**
 * Single top-level dialog mutex for staff web-carwash.
 * Lane modals (notification / Way) and route-driven feature auto-opens consult this service
 * so dialogs do not open on top of each other. Lane LPR popups that lose the mutex
 * are discarded (stored in history); route-driven feature auto-opens may defer.
 */
@Injectable({ providedIn: 'root' })
export class DialogMutexService {
  private readonly stack: DialogMutexFrame[] = [];
  private readonly deferredRuns: { owner: TopLevelDialogOwner; run: () => void }[] = [];
  private readonly releaseSubject = new Subject<TopLevelDialogOwner>();

  /** Emits the owner that was released (after stack update). */
  readonly releases$ = this.releaseSubject.asObservable();

  getActiveOwner(): TopLevelDialogOwner | null {
    const top = this.stack[this.stack.length - 1];
    return top?.owner ?? null;
  }

  /** True when any top-level dialog is registered. */
  isBusy(): boolean {
    return this.stack.length > 0;
  }

  /** True when a lane LPR / Way dialog owns the mutex (feature auto-opens should defer). */
  isLaneDialogOpen(): boolean {
    return this.stack.some(
      frame => frame.owner === 'notification-modal' || frame.owner === 'way-purchase-modal'
    );
  }

  hasOwner(owner: TopLevelDialogOwner): boolean {
    return this.stack.some(frame => frame.owner === owner);
  }

  /**
   * Try to acquire the mutex for `owner`.
   * Same-owner re-acquire is allowed (content replace within the same dialog).
   * Cross-owner acquire fails unless an explicit {@link DIALOG_STACK_EXCEPTIONS} applies.
   */
  tryAcquire(owner: TopLevelDialogOwner, options: DialogMutexAcquireOptions = {}): boolean {
    const top = this.stack[this.stack.length - 1];
    if (!top) {
      this.stack.push({ owner, exception: options.exception });
      return true;
    }

    if (top.owner === owner) {
      return true;
    }

    if (
      options.exception === DIALOG_STACK_EXCEPTIONS.WAY_ON_NOTIFICATION &&
      top.owner === 'notification-modal' &&
      owner === 'way-purchase-modal'
    ) {
      this.stack.push({ owner, exception: options.exception });
      return true;
    }

    return false;
  }

  /** Drop pending deferred opens for `owner` (e.g. superseded by a replace-style force-acquire). */
  cancelDeferred(owner: TopLevelDialogOwner): void {
    for (let i = this.deferredRuns.length - 1; i >= 0; i -= 1) {
      if (this.deferredRuns[i].owner === owner) {
        this.deferredRuns.splice(i, 1);
      }
    }
  }

  /**
   * Force `owner` to the top: clears any other owners, then acquires.
   * Used for `behavior: 'replace'` opens (user reopen, checkout transition, staff quick sale).
   */
  forceAcquire(owner: TopLevelDialogOwner): void {
    this.cancelDeferred(owner);
    while (this.stack.length > 0) {
      const frame = this.stack.pop();
      if (frame && frame.owner !== owner) {
        this.releaseSubject.next(frame.owner);
      }
    }
    this.stack.push({ owner });
  }

  /**
   * Release one frame for `owner` (most recent match). No-op if not present.
   */
  release(owner: TopLevelDialogOwner): void {
    for (let i = this.stack.length - 1; i >= 0; i -= 1) {
      if (this.stack[i].owner === owner) {
        this.stack.splice(i, 1);
        this.releaseSubject.next(owner);
        this.flushDeferred();
        return;
      }
    }
  }

  /** Clear the entire mutex (tests / forced resets). */
  clear(): void {
    const owners = this.stack.map(frame => frame.owner);
    this.stack.length = 0;
    this.deferredRuns.length = 0;
    for (const owner of owners) {
      this.releaseSubject.next(owner);
    }
  }

  /**
   * Queue a deferred open until the mutex is free for that owner (or empty).
   * Only the first deferred run per flush cycle is executed.
   */
  defer(owner: TopLevelDialogOwner, run: () => void): void {
    this.deferredRuns.push({ owner, run });
  }

  /** Run the next deferred open once no top-level dialog holds the mutex. */
  flushDeferred(): void {
    if (this.isBusy() || this.deferredRuns.length === 0) {
      return;
    }
    const next = this.deferredRuns.shift();
    next?.run();
  }
}
